import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import {
	FALLBACK_LANGUAGE,
	LANGUAGE_STORAGE_KEY,
	resolveLanguage,
	SUPPORTED_LANGUAGES,
	type SupportedLanguage,
} from './language'
import authEn from './locales/en/auth.json'
import complianceEn from './locales/en/compliance.json'
import contentEn from './locales/en/content.json'
import en from './locales/en/customers.json'
import importsEn from './locales/en/imports.json'
import offersEn from './locales/en/offers.json'
import organizationEn from './locales/en/organization.json'
import productsEn from './locales/en/products.json'
import securityEn from './locales/en/security.json'
import settingsEn from './locales/en/settings.json'
import authEs from './locales/es/auth.json'
import complianceEs from './locales/es/compliance.json'
import contentEs from './locales/es/content.json'
import es from './locales/es/customers.json'
import importsEs from './locales/es/imports.json'
import offersEs from './locales/es/offers.json'
import organizationEs from './locales/es/organization.json'
import productsEs from './locales/es/products.json'
import securityEs from './locales/es/security.json'
import settingsEs from './locales/es/settings.json'
import authGl from './locales/gl/auth.json'
import complianceGl from './locales/gl/compliance.json'
import contentGl from './locales/gl/content.json'
import gl from './locales/gl/customers.json'
import importsGl from './locales/gl/imports.json'
import offersGl from './locales/gl/offers.json'
import organizationGl from './locales/gl/organization.json'
import productsGl from './locales/gl/products.json'
import securityGl from './locales/gl/security.json'
import settingsGl from './locales/gl/settings.json'
import authPt from './locales/pt/auth.json'
import compliancePt from './locales/pt/compliance.json'
import contentPt from './locales/pt/content.json'
import pt from './locales/pt/customers.json'
import importsPt from './locales/pt/imports.json'
import offersPt from './locales/pt/offers.json'
import organizationPt from './locales/pt/organization.json'
import productsPt from './locales/pt/products.json'
import securityPt from './locales/pt/security.json'
import settingsPt from './locales/pt/settings.json'

/** The customer screens (TO-418). */
export const CUSTOMERS_NAMESPACE = 'customers'
/** The product, category and locale-settings screens (TO-416). */
export const PRODUCTS_NAMESPACE = 'products'
/** The content admin screens: texts, approvals, types and assignments (TO-420). */
export const CONTENT_NAMESPACE = 'content'
/** The console's Organization section: tree, units, locations, assignments and types (TO-389). */
export const ORGANIZATION_NAMESPACE = 'organization'
/** The sign-in surfaces: login, tenant login, callback and sign-in status screens (TO-361). */
export const AUTH_NAMESPACE = 'auth'
/** The offer list, wizard (TO-122 Step 1 onwards) and generation-status screens (TO-466). */
export const OFFERS_NAMESPACE = 'offers'
/** The GDPR compliance admin screens: deletions, legal holds and access requests (TO-232). */
export const COMPLIANCE_NAMESPACE = 'compliance'
/** The Admin → Imports monitoring screens: executions list and drill-down (TO-399). */
export const IMPORTS_NAMESPACE = 'imports'
/** The Settings Security section and the TOTP enrolment journey (TO-63). */
export const SECURITY_NAMESPACE = 'security'
/** The Settings self-service sections: profile, contact on offers, sessions, notifications (TO-197). */
export const SETTINGS_NAMESPACE = 'settings'

export const resources = {
	es: {
		customers: es,
		products: productsEs,
		content: contentEs,
		organization: organizationEs,
		offers: offersEs,
		compliance: complianceEs,
		imports: importsEs,
		auth: authEs,
		security: securityEs,
		settings: settingsEs,
	},
	gl: {
		customers: gl,
		products: productsGl,
		content: contentGl,
		organization: organizationGl,
		offers: offersGl,
		compliance: complianceGl,
		imports: importsGl,
		auth: authGl,
		security: securityGl,
		settings: settingsGl,
	},
	en: {
		customers: en,
		products: productsEn,
		content: contentEn,
		organization: organizationEn,
		offers: offersEn,
		compliance: complianceEn,
		imports: importsEn,
		auth: authEn,
		security: securityEn,
		settings: settingsEn,
	},
	pt: {
		customers: pt,
		products: productsPt,
		content: contentPt,
		organization: organizationPt,
		offers: offersPt,
		compliance: compliancePt,
		imports: importsPt,
		auth: authPt,
		security: securityPt,
		settings: settingsPt,
	},
} as const

const storedLanguage = (): string | null => {
	try {
		return globalThis.localStorage?.getItem(LANGUAGE_STORAGE_KEY) ?? null
	} catch {
		return null
	}
}

const browserLanguages = (): readonly string[] => globalThis.navigator?.languages ?? []

/** Initialise i18next once, with the resolved language (stored → browser → es). */
export const i18n = i18next.createInstance()
void i18n.use(initReactI18next).init({
	resources,
	lng: resolveLanguage(storedLanguage(), browserLanguages()),
	fallbackLng: FALLBACK_LANGUAGE,
	supportedLngs: [...SUPPORTED_LANGUAGES],
	defaultNS: CUSTOMERS_NAMESPACE,
	ns: [
		CUSTOMERS_NAMESPACE,
		PRODUCTS_NAMESPACE,
		CONTENT_NAMESPACE,
		ORGANIZATION_NAMESPACE,
		OFFERS_NAMESPACE,
		COMPLIANCE_NAMESPACE,
		IMPORTS_NAMESPACE,
		AUTH_NAMESPACE,
		SECURITY_NAMESPACE,
		SETTINGS_NAMESPACE,
	],
	interpolation: { escapeValue: false },
	returnNull: false,
})

/** Switch the UI language immediately and remember the choice. */
export const changeLanguage = async (language: SupportedLanguage): Promise<void> => {
	try {
		globalThis.localStorage?.setItem(LANGUAGE_STORAGE_KEY, language)
	} catch {
		// Private mode / storage disabled: the switch still applies for this session.
	}
	await i18n.changeLanguage(language)
}

/** Forget the stored choice and fall back to the browser language, else `es` (TO-197 "Automatic"). */
export const resetLanguage = async (): Promise<void> => {
	try {
		globalThis.localStorage?.removeItem(LANGUAGE_STORAGE_KEY)
	} catch {
		// Storage disabled: nothing was stored.
	}
	await i18n.changeLanguage(resolveLanguage(null, browserLanguages()))
}
